import {
  Box,
  FormControl,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Checkbox,
} from '@mui/material';

import { CcfTypography } from '../core/ccf-typography/ccf-typography';

export interface CcfActionEditorMultiselectDropdownProps {
  handleChange?: (configuration: any) => void;
  handleOnOpen?: () => void;
  options?: any[];
  className?: string;
  value?: string;
  label?: string;
  singleSelect?: boolean;
  selected?: any[];
}

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 250,
    },
  },
};

const KEY_OF_DISPLAY_OPTION = '__multiselect_selected_tracker';

/**
 * Action Editor component for multiselect dropdown
 * @param props - CcfActionEditorMultiselectDropdownProps
 * @returns component for multiselect dropdown
 * @example <CcfActionEditorMultiselectDropdown />
 */
export function CcfActionEditorMultiselectDropdown(props: CcfActionEditorMultiselectDropdownProps) {
  const {
    options = [],
    className,
    value = 'value',
    label = 'label',
    handleChange,
    handleOnOpen,
    selected = [],
  } = props;

  const singleSelect = Object.keys(props).includes('singleSelect');
  const areAllSelected = selected.length === options.length;

  /**
   * Function to handle logic for when the component is in single select mode.
   * @example handleOnChangeOfSelectForSingleSelect()
   */
  const handleOnChangeOfSelectForSingleSelect = (event: any) => {
    const {
      target: { value: selection },
    } = event;

    let output = [selection];

    if (selected.includes(selection)) {
      output = [];
    }

    handleChange && typeof handleChange === 'function' && handleChange(output[0]);
  };

  /**
   * Function to handle logic for when the component is in multi select mode.
   * @example handleOnChangeOfSelectForMultiSelect()
   */
  const handleOnChangeOfSelectForMultiSelect = (event: any) => {
    let items = [];

    const {
      target: { value: selections },
    } = event;

    const selection = selections[selections.length - 1];

    if (selections.includes('all')) {
      if (options.length === selected.length) {
        items = [];
      } else {
        items = options.map((option) => option[value]);
      }
    } else {
      items = [...selected];

      if (selected.includes(selection)) {
        items = items.filter((item) => item !== selection);
      } else {
        items.push(selection);
      }
    }

    const output = [...items];

    handleChange && typeof handleChange === 'function' && handleChange(output);
  };

  /**
   * Function to determine which select mode function to execute.
   * @example onChangeOfSelect()
   */
  const onChangeOfSelect = (event: any) => {
    if (singleSelect) {
      handleOnChangeOfSelectForSingleSelect(event);
    } else {
      handleOnChangeOfSelectForMultiSelect(event);
    }
  };

  /**
   * Function to handle logic for when the select component is opened.
   * @example onOpenOfSelect()
   */
  const onOpenOfSelect = () => {
    handleOnOpen && typeof handleOnOpen === 'function' && handleOnOpen();
  };

  /**
   * Function to set the selection count.
   * @example determineSelectionCount()
   */
  const determineSelectionCount = () => {
    if (selected.includes(KEY_OF_DISPLAY_OPTION)) {
      return selected.length - 1;
    }

    return selected.length;
  };

  const selectionCount = determineSelectionCount();

  return (
    <Box className={className}>
      <FormControl>
        <Select
          value={singleSelect ? KEY_OF_DISPLAY_OPTION : [KEY_OF_DISPLAY_OPTION]}
          multiple={!singleSelect}
          onChange={onChangeOfSelect}
          onOpen={onOpenOfSelect}
          MenuProps={MenuProps}
        >
          <MenuItem value={KEY_OF_DISPLAY_OPTION} sx={{ display: 'none' }}>
            <CcfTypography>{selectionCount} item(s) selected.</CcfTypography>
          </MenuItem>

          {!singleSelect && (
            <MenuItem value="all">
              <ListItemIcon>
                <Checkbox checked={areAllSelected} />
              </ListItemIcon>

              <ListItemText primary="Select All" />
            </MenuItem>
          )}

          {options.map((option, index) => (
            <MenuItem value={option[value]} key={`multiselect-option-${index}`}>
              {!singleSelect && (
                <ListItemIcon>
                  <Checkbox checked={selected.includes(option[value])} />
                </ListItemIcon>
              )}

              <ListItemText primary={option[label]} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default CcfActionEditorMultiselectDropdown;
